import { useState, useRef } from "react";
import { toast } from "sonner";
import TextareaAutosize from "react-textarea-autosize";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  ArrowLeft,
  Heart,
  Camera,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import api, { formatApiError } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

const NOMINATE_HANDS =
  "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/42a8116b73e861388f39cbc24ef452fc82b323842dc2db21883315003dd09d47.jpeg";

const MX_STATES = [
  "Aguascalientes","Baja California","Baja California Sur","Campeche","CDMX",
  "Chiapas","Chihuahua","Coahuila","Colima","Durango","Estado de México",
  "Guanajuato","Guerrero","Hidalgo","Jalisco","Michoacán","Morelos","Nayarit",
  "Nuevo León","Oaxaca","Puebla","Querétaro","Quintana Roo","San Luis Potosí",
  "Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán",
  "Zacatecas",
];

const INDUSTRIES = [
  "Manufactura","Artesanía","Tecnología","Agricultura","Ciencia","Gastronomía",
  "Cultura","Arte","Educación","Salud","Finanzas","Medios","Otra",
];

const RELATIONSHIP = ["Yo misma","Colega","Amiga","Familia","Conocida","Otra"];

const EMPTY = {
  nominee_name: "", nominee_email: "", nominee_phone: "",
  nominee_city: "", nominee_state: "", nominee_industry: "",
  nominee_social: "",
  story_summary: "", community_impact: "",
  nominator_name: "", nominator_email: "", nominator_phone: "",
  relationship: "", nominee_consent: false,
  website: "",
};

const STEPS = [
  { n: 1, label: "Sobre ella" },
  { n: 2, label: "Su historia" },
  { n: 3, label: "Tú" },
];

export default function Nominate() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  const upd = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validateStep = (n) => {
    const e = {};
    if (n === 1) {
      if (!form.nominee_name.trim()) e.nominee_name = "Nombre requerido";
      if (!form.nominee_city.trim()) e.nominee_city = "Ciudad requerida";
      if (!form.nominee_industry) e.nominee_industry = "Industria requerida";
    }
    if (n === 2) {
      if (form.story_summary.trim().length < 80)
        e.story_summary = "Mínimo 80 caracteres";
      if (form.community_impact.trim().length < 80)
        e.community_impact = "Mínimo 80 caracteres";
    }
    if (n === 3) {
      if (!form.nominator_name.trim()) e.nominator_name = "Nombre requerido";
      if (!/^\S+@\S+\.\S+$/.test(form.nominator_email))
        e.nominator_email = "Email válido requerido";
      if (!form.relationship) e.relationship = "Selecciona una opción";
      if (!form.nominee_consent) e.nominee_consent = "Confirmación requerida";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => s + 1);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const prev = () => {
    setStep((s) => s - 1);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setSubmitting(true);
    try {
      // Strip empty optional fields — Pydantic EmailStr rejects "" for Optional[EmailStr]
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "string") {
          const trimmed = v.trim();
          if (trimmed !== "") payload[k] = trimmed;
        } else {
          payload[k] = v;
        }
      }
      // Required fields that must always be present
      payload.nominee_consent = !!form.nominee_consent;
      payload.attribution = getAttribution();
      const { data } = await api.post("/submissions/nominate", payload);
      trackEvent("form_submit", { form_type: "nominate" });
      toast.success(data.message);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err.response?.status === 429
          ? "Demasiadas solicitudes. Intenta en un minuto."
          : formatApiError(err.response?.data?.detail) ||
            "Error al enviar la postulación.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <SiteLayout>
        <></>
        <main className="min-h-[80vh] bg-charcoal text-cream flex items-center pt-28" data-testid="nominate-success">
          <Container>
            <div className="max-w-3xl mx-auto text-center py-20">
              <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-8" strokeWidth={1} />
              <Eyebrow tone="gold" className="mb-5 block">Postulación recibida</Eyebrow>
              <h1 className="font-serif font-light leading-[1.05] text-[clamp(2.5rem,5vw,4.5rem)] mb-8">
                Recibimos <span className="italic">su historia.</span>
              </h1>
              <p className="text-lg md:text-xl text-cream/80 max-w-2xl mx-auto leading-relaxed">
                Nuestro equipo editorial la revisará en los próximos <strong>10 días hábiles</strong>.
                Si avanza a producción, les contactaremos directamente para coordinar.
              </p>
              <a
                href="/stories"
                className="inline-flex items-center gap-2 mt-12 text-gold hover:text-cream transition-colors"
              >
                Explorar otras historias
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Container>
        </main>
      </SiteLayout>
    );
  }

  const input = "w-full bg-transparent border-b border-charcoal/30 py-3 text-base text-charcoal placeholder:text-charcoal/40 focus:border-terracotta focus:outline-none transition-colors";
  const inputDark = "w-full bg-transparent border-b border-cream/35 py-3 text-base text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none transition-colors";
  const lab = "text-[11px] uppercase tracking-[0.22em] text-cream/60 block mb-2";
  const err = (k) => errors[k] && <p className="text-[11px] text-[#E88060] mt-1">{errors[k]}</p>;

  return (
    <SiteLayout transparentHeader>
      <></>

      {/* HERO 50vh */}
      <section className="relative h-[60vh] min-h-[480px] w-full overflow-hidden bg-charcoal" data-testid="nominate-hero">
        <img src={NOMINATE_HANDS} alt="Postula una historia" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-transparent to-charcoal/95" />
        <Container className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-20">
          <div className="max-w-4xl animate-fade-up">
            <Eyebrow tone="gold" className="mb-5">Postula una historia</Eyebrow>
            <h1 className="font-serif font-light text-cream leading-[0.98] tracking-tight" style={{ fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }}>
              ¿Conoces a una mujer que esté<br /><span className="italic text-terracotta">moviendo México?</span>
            </h1>
            <p className="text-cream/85 mt-7 text-base md:text-lg max-w-2xl leading-relaxed">
              Nuestro equipo editorial revisa cada postulación. Si la historia se alinea con
              la temporada, la producimos junto a su protagonista. <strong>Sin costo para ella.</strong>
            </p>
          </div>
        </Container>
      </section>

      {/* Qué buscamos */}
      <section className="bg-cream py-20 md:py-28" data-testid="nominate-criteria">
        <Container>
          <Eyebrow tone="terracotta" className="mb-8 block text-center">Qué buscamos</Eyebrow>
          <div className="grid md:grid-cols-3 gap-10 md:gap-14 max-w-5xl mx-auto">
            {[
              { icon: Heart, title: "Historia con impacto real", desc: "Una mujer cuyas acciones ya están transformando a su comunidad, industria o territorio — no promesas, hechos documentables." },
              { icon: Camera, title: "Voz documentable", desc: "Protagonista dispuesta a participar en entrevista de video y sesión de fotos editoriales en su contexto cotidiano." },
              { icon: Sparkles, title: "Alineación editorial", desc: "Liderazgo, innovación, cultura, ciencia, artesanía, tecnología, gastronomía, agricultura, impacto social." },
            ].map((it, i) => (
              <div key={i} className="text-center">
                <it.icon className="h-8 w-8 text-terracotta mx-auto mb-5" strokeWidth={1.2} />
                <h3 className="font-serif text-xl md:text-2xl text-charcoal mb-3 font-light">{it.title}</h3>
                <p className="text-charcoal/70 leading-relaxed text-sm md:text-base">{it.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FORM 3 STEPS */}
      <section ref={formRef} className="bg-charcoal text-cream py-20 md:py-28" data-testid="nominate-form-section">
        <Container>
          <div className="max-w-[720px] mx-auto">
            {/* progress */}
            <div className="flex items-center gap-3 mb-12" data-testid="nominate-steps">
              {STEPS.map((s, i) => (
                <div key={s.n} className="flex items-center gap-3 flex-1">
                  <div className={`h-8 w-8 rounded-full border flex items-center justify-center text-xs font-medium transition-colors ${
                    s.n === step ? "bg-terracotta border-terracotta text-cream"
                    : s.n < step ? "bg-gold border-gold text-charcoal"
                    : "border-cream/30 text-cream/50"
                  }`}>
                    {s.n < step ? "✓" : s.n}
                  </div>
                  <span className={`text-xs uppercase tracking-[0.2em] ${s.n === step ? "text-gold" : "text-cream/50"}`}>
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px flex-1 ${s.n < step ? "bg-gold" : "bg-cream/15"}`} />
                  )}
                </div>
              ))}
            </div>

            <form onSubmit={submit} data-testid="nominate-form" className="space-y-6" noValidate>
              {/* STEP 1 */}
              {step === 1 && (
                <div className="space-y-6 animate-fade-up" data-testid="step-1">
                  <h2 className="font-serif text-3xl md:text-4xl font-light mb-6">Sobre ella</h2>

                  <div>
                    <label className={lab}>Nombre completo *</label>
                    <input className={inputDark} value={form.nominee_name} onChange={(e) => upd("nominee_name", e.target.value)} data-testid="f-nominee-name" placeholder="Ana Rivera" />
                    {err("nominee_name")}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={lab}>Ciudad *</label>
                      <input className={inputDark} value={form.nominee_city} onChange={(e) => upd("nominee_city", e.target.value)} data-testid="f-nominee-city" />
                      {err("nominee_city")}
                    </div>
                    <div>
                      <label className={lab}>Estado</label>
                      <select className={`${inputDark} appearance-none`} value={form.nominee_state} onChange={(e) => upd("nominee_state", e.target.value)}>
                        <option value="" className="bg-charcoal">—</option>
                        {MX_STATES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={lab}>Industria *</label>
                    <select className={`${inputDark} appearance-none`} value={form.nominee_industry} onChange={(e) => upd("nominee_industry", e.target.value)} data-testid="f-nominee-industry">
                      <option value="" className="bg-charcoal">Selecciona…</option>
                      {INDUSTRIES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}
                    </select>
                    {err("nominee_industry")}
                  </div>

                  <div>
                    <label className={lab}>Redes sociales (una por línea, opcional)</label>
                    <TextareaAutosize className={inputDark} minRows={2} value={form.nominee_social} onChange={(e) => upd("nominee_social", e.target.value)} placeholder="https://instagram.com/...&#10;https://linkedin.com/in/..." />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={lab}>Email de ella (opcional)</label>
                      <input type="email" className={inputDark} value={form.nominee_email} onChange={(e) => upd("nominee_email", e.target.value)} />
                    </div>
                    <div>
                      <label className={lab}>Teléfono de ella (opcional)</label>
                      <input className={inputDark} value={form.nominee_phone} onChange={(e) => upd("nominee_phone", e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div className="space-y-6 animate-fade-up" data-testid="step-2">
                  <h2 className="font-serif text-3xl md:text-4xl font-light mb-6">Su historia</h2>

                  <div>
                    <div className="flex justify-between items-baseline">
                      <label className={lab}>Resumen de la historia *</label>
                      <span className={`text-[11px] ${form.story_summary.length >= 80 ? "text-gold" : "text-cream/40"}`}>
                        {form.story_summary.length} / 2000
                      </span>
                    </div>
                    <TextareaAutosize className={inputDark} minRows={4} maxLength={2000} value={form.story_summary} onChange={(e) => upd("story_summary", e.target.value)} data-testid="f-story-summary" placeholder="Qué hace, por qué importa, qué la hace única." />
                    {err("story_summary")}
                  </div>

                  <div>
                    <div className="flex justify-between items-baseline">
                      <label className={lab}>Impacto en su comunidad *</label>
                      <span className={`text-[11px] ${form.community_impact.length >= 80 ? "text-gold" : "text-cream/40"}`}>
                        {form.community_impact.length} / 1500
                      </span>
                    </div>
                    <TextareaAutosize className={inputDark} minRows={4} maxLength={1500} value={form.community_impact} onChange={(e) => upd("community_impact", e.target.value)} data-testid="f-community-impact" placeholder="Qué ha transformado concretamente. Comunidades, mujeres impactadas, métricas, reconocimientos." />
                    {err("community_impact")}
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <div className="space-y-6 animate-fade-up" data-testid="step-3">
                  <h2 className="font-serif text-3xl md:text-4xl font-light mb-6">Tú</h2>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={lab}>Tu nombre *</label>
                      <input className={inputDark} value={form.nominator_name} onChange={(e) => upd("nominator_name", e.target.value)} data-testid="f-nominator-name" />
                      {err("nominator_name")}
                    </div>
                    <div>
                      <label className={lab}>Tu email *</label>
                      <input type="email" className={inputDark} value={form.nominator_email} onChange={(e) => upd("nominator_email", e.target.value)} data-testid="f-nominator-email" />
                      {err("nominator_email")}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={lab}>Tu teléfono (opcional)</label>
                      <input className={inputDark} value={form.nominator_phone} onChange={(e) => upd("nominator_phone", e.target.value)} />
                    </div>
                    <div>
                      <label className={lab}>Relación con ella *</label>
                      <select className={`${inputDark} appearance-none`} value={form.relationship} onChange={(e) => upd("relationship", e.target.value)} data-testid="f-relationship">
                        <option value="" className="bg-charcoal">Selecciona…</option>
                        {RELATIONSHIP.map((r) => <option key={r} value={r} className="bg-charcoal">{r}</option>)}
                      </select>
                      {err("relationship")}
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-4">
                    <input type="checkbox" checked={form.nominee_consent} onChange={(e) => upd("nominee_consent", e.target.checked)} data-testid="f-consent"
                      className="mt-1 h-4 w-4 accent-terracotta" />
                    <span className="text-sm text-cream/80 leading-relaxed">
                      Confirmo que la nominada sabe de esta postulación y está dispuesta a participar en una eventual producción editorial.
                    </span>
                  </label>
                  {err("nominee_consent")}

                  {/* honeypot */}
                  <div aria-hidden="true" style={{ position: "absolute", left: -10000, width: 1, height: 1, overflow: "hidden" }}>
                    <label htmlFor="hp">Sitio web</label>
                    <input id="hp" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => upd("website", e.target.value)} />
                  </div>
                </div>
              )}

              {/* nav */}
              <div className="flex flex-col sm:flex-row justify-between gap-4 pt-8">
                {step > 1 ? (
                  <button type="button" onClick={prev} data-testid="step-prev" className="inline-flex items-center gap-2 text-sm text-cream/70 hover:text-gold transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Anterior
                  </button>
                ) : <span />}
                {step < 3 ? (
                  <button type="button" onClick={next} data-testid="step-next" className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm hover:bg-[#A64826] transition-all duration-[400ms]">
                    Siguiente <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="submit" disabled={submitting} data-testid="nominate-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60">
                    {submitting ? "Enviando…" : "Enviar postulación"}
                    {!submitting && <ArrowRight className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </form>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-cream py-20 md:py-28" data-testid="nominate-faq">
        <Container>
          <div className="max-w-3xl mx-auto">
            <Eyebrow tone="terracotta" className="mb-5">Preguntas frecuentes</Eyebrow>
            <h2 className="font-serif font-light text-3xl md:text-4xl mb-10">Todo lo que necesitas saber.</h2>
            <Accordion type="single" collapsible className="border-t border-charcoal/10">
              {[
                ["¿Tiene costo postular o ser producida?", "No. Completamente gratis para la nominada. Toda la producción corre por cuenta de Mujeres Motor y sus aliados."],
                ["¿Cuánto toma el proceso si avanza a producción?", "Entre 6 y 10 semanas desde la preproducción hasta el lanzamiento, dependiendo de disponibilidad y locación."],
                ["¿Puedo postularme a mí misma?", "Sí. Es común. Selecciona 'Yo misma' como relación en el tercer paso."],
                ["¿Qué pasa si no avanza mi postulación?", "Te notificamos por email. Muchas historias regresan en temporadas siguientes — el archivo crece cada semestre."],
              ].map(([q, a], i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-charcoal/10">
                  <AccordionTrigger className="text-left text-lg md:text-xl font-serif py-5 hover:no-underline hover:text-terracotta">{q}</AccordionTrigger>
                  <AccordionContent className="text-base text-charcoal/75 leading-relaxed pb-6">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
